import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ForkliftCard from "@/components/ForkliftCard";
import { BRANDS, FUELS, MACHINE_TYPES } from "@/lib/data";
import { Filter, Search } from "lucide-react";

function ListingPage({ kind, title, tagline, intro }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ brand: "", fuel_type: "", machine_type: "", q: "" });

  useEffect(() => {
    setLoading(true);
    const params = { listing_type: kind };
    if (f.brand) params.brand = f.brand;
    if (f.fuel_type) params.fuel_type = f.fuel_type;
    if (f.machine_type) params.machine_type = f.machine_type;
    api.get("/forklifts", { params }).then(r => setItems(r.data)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.brand, f.fuel_type, f.machine_type, kind]);

  const filtered = items.filter(x => !f.q || `${x.title} ${x.brand} ${x.model}`.toLowerCase().includes(f.q.toLowerCase()));

  let results;
  if (loading) {
    results = <div className="text-center py-20 text-[#666] uppercase tracking-widest text-sm">Loading…</div>;
  } else if (filtered.length === 0) {
    results = (
      <div className="ff-card p-10 text-center">
        <h3 className="font-display text-3xl uppercase">Can't find the forklift you need?</h3>
        <p className="text-sm text-[#555] mt-3 max-w-lg mx-auto">Tell us what capacity and forklift type you're looking for and Forkfix can contact you about suitable equipment.</p>
        <a href="/contact" className="ff-btn-primary mt-6 inline-flex">Tell us what you need</a>
      </div>
    );
  } else {
    results = (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="forklift-grid">
        {filtered.map(x => <ForkliftCard key={x.forklift_id} f={x} mode={kind} />)}
      </div>
    );
  }

  return (
    <div>
      <section className="bg-[#111111] text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 ff-diagonal-bg">
          <div className="text-xs uppercase tracking-widest text-[#D71920] font-bold">{tagline}</div>
          <h1 className="font-display text-5xl md:text-7xl uppercase mt-3">{title}</h1>
          <p className="mt-4 max-w-3xl text-white/75 text-lg">{intro}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="ff-card p-5 mb-8">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold mb-4"><Filter className="w-4 h-4 text-[#D71920]" /> Filter Inventory</div>
          <div className="grid md:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
              <input value={f.q} onChange={e => setF({ ...f, q: e.target.value })} placeholder="Search forklifts…" className="ff-input !pl-9" data-testid="filter-search" />
            </div>
            <select value={f.brand} onChange={e => setF({ ...f, brand: e.target.value })} className="ff-input" data-testid="filter-brand">
              <option value="">All brands</option>{BRANDS.map(b => <option key={b}>{b}</option>)}
            </select>
            <select value={f.fuel_type} onChange={e => setF({ ...f, fuel_type: e.target.value })} className="ff-input" data-testid="filter-fuel">
              <option value="">All fuel types</option>{FUELS.map(b => <option key={b}>{b}</option>)}
            </select>
            <select value={f.machine_type} onChange={e => setF({ ...f, machine_type: e.target.value })} className="ff-input" data-testid="filter-machine">
              <option value="">All machine types</option>{MACHINE_TYPES.map(b => <option key={b}>{b}</option>)}
            </select>
          </div>
        </div>

        {results}
      </section>
    </div>
  );
}

export function Sale() { return <ListingPage kind="sale" tagline="Used Equipment" title="Used forklifts for sale" intro="Browse used forklifts available from Forkfix. Our inventory includes LPG, diesel and electric forklifts suitable for warehousing, logistics, construction and industrial applications." />; }
export function Hire() { return <ListingPage kind="hire" tagline="Rental Fleet" title="Forklift hire Brisbane" intro="Flexible forklift rental solutions for businesses requiring additional equipment for short-term jobs, temporary demand, breakdown cover or longer-term operations." />; }
export default Sale;
